import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLaunchParams } from "@telegram-apps/sdk-react";
import {
  usePriceAlertConfigs,
  usePriceAlertStats,
  useCreatePriceAlertConfig,
  useUpdatePriceAlertConfig,
  useDeletePriceAlertConfig,
} from "@/hooks/usePriceAlerts";
import {
  PriceAlertConfigData,
  CreatePriceAlertConfigPayload,
  UpdatePriceAlertConfigPayload,
} from "@/api/price-alerts";
import { PriceAlertConfigTile } from "@/components/PriceAlertConfigTile";
import { PriceAlertConfigDrawer } from "@/components/PriceAlertConfigDrawer";
import { PriceAlertStatsCards } from "@/components/PriceAlertStatsCards";
import { ReferralBottomNav } from "@/components/ReferralBottomNav";
import { Loader2, ArrowLeft, Plus } from "lucide-react";

export const PriceAlertConfigPageMobile: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const launchParams = useLaunchParams();
  const userId = launchParams.initData?.user?.id?.toString() || "";

  const [showAddDrawer, setShowAddDrawer] = useState(false);
  const [editingConfig, setEditingConfig] = useState<PriceAlertConfigData | null>(null);

  const { data: configs, isLoading } = usePriceAlertConfigs(userId);
  const { data: statsData, isLoading: isLoadingStats } = usePriceAlertStats(userId);
  const createMutation = useCreatePriceAlertConfig(userId);
  const updateMutation = useUpdatePriceAlertConfig(userId);
  const deleteMutation = useDeletePriceAlertConfig(userId);

  useEffect(() => {
    if (searchParams.get("add") === "true") {
      setShowAddDrawer(true);
    }
  }, [searchParams]);

  const handleCreate = (payload: CreatePriceAlertConfigPayload | UpdatePriceAlertConfigPayload) => {
    createMutation.mutate(payload as CreatePriceAlertConfigPayload, {
      onSuccess: () => setShowAddDrawer(false),
    });
  };

  const handleUpdate = (payload: CreatePriceAlertConfigPayload | UpdatePriceAlertConfigPayload) => {
    if (!editingConfig) return;
    updateMutation.mutate(
      { configId: editingConfig.id, payload: payload as UpdatePriceAlertConfigPayload },
      { onSuccess: () => setEditingConfig(null) },
    );
  };

  const handleDelete = (configId: string) => {
    if (globalThis.confirm("Delete this alert configuration?")) {
      deleteMutation.mutate(configId);
    }
  };

  return (
    <div className="min-h-screen bg-primary flex flex-col">
      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-2xl mx-auto">
          {/* Header with back button */}
          <div className="flex items-center gap-3 mb-4">
            <button
              onClick={() => navigate("/price-alerts")}
              className="p-2 rounded-lg hover:bg-gray-600/20 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-text-primary" />
            </button>
            <h1 className="text-xl font-bold text-text-primary">
              Manage Alerts
            </h1>
          </div>

          <div className="mb-4">
            <PriceAlertStatsCards stats={statsData} isLoading={isLoadingStats} />
          </div>

          {isLoading && (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-accent" />
            </div>
          )}

          {!isLoading && (!configs || configs.length === 0) && (
            <div className="text-center py-12">
              <p className="text-text-secondary">No alert configurations yet</p>
              <p className="text-text-secondary text-sm mt-1">
                Tap the + button to create your first alert
              </p>
            </div>
          )}

          {!isLoading && configs && configs.length > 0 && (
            <div className="space-y-3">
              {configs.map((config) => (
                <PriceAlertConfigTile
                  key={config.id}
                  config={config}
                  onEdit={(c) => setEditingConfig(c)}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Floating Add Button */}
      <button
        onClick={() => setShowAddDrawer(true)}
        className="fixed bottom-20 right-4 z-40 w-14 h-14 rounded-full bg-accent hover:bg-accent/80 flex items-center justify-center shadow-lg transition-all hover:scale-105 active:scale-95"
        aria-label="Add price alert"
      >
        <Plus className="w-6 h-6 text-white" />
      </button>

      <ReferralBottomNav />

      {/* Add Drawer */}
      <PriceAlertConfigDrawer
        open={showAddDrawer}
        onOpenChange={setShowAddDrawer}
        onSubmit={handleCreate}
        isLoading={createMutation.isPending}
      />

      {/* Edit Drawer */}
      <PriceAlertConfigDrawer
        open={!!editingConfig}
        onOpenChange={(open) => { if (!open) setEditingConfig(null); }}
        config={editingConfig}
        onSubmit={handleUpdate}
        isLoading={updateMutation.isPending}
      />
    </div>
  );
};
